import {
	CloseOutlined,
	ExpandLess,
	ExpandMore,
} from "@mui/icons-material";
import PhoneCallbackOutlinedIcon from "@mui/icons-material/PhoneCallbackOutlined";
import {
	Box,
	Collapse,
	Drawer,
	IconButton,
	Typography,
} from "@mui/material";
import React, { FC, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDate } from "../../../../utils/helpers";
import CallConversation from "./call-conversation";

interface CallGroupProps {
	label: string;
	defaultExpanded: boolean;
	children: ReactNode;
}

const CallGroup: FC<CallGroupProps> = ({
	label,
	defaultExpanded,
	children,
}) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	return (
		<Box
			sx={{
				border: "1px solid rgba(47,77,139,0.15)",
				borderRadius: "8px",
			}}
		>
			<Box
				onClick={() => setIsExpanded(!isExpanded)}
				sx={{
					display: "flex",
					alignItems: "center",
					gap: "8px",
					padding: "8px 12px",
					cursor: "pointer",
					"&:hover": { background: "#F5F7FB" },
					borderRadius: "8px",
				}}
			>
				<PhoneCallbackOutlinedIcon
					sx={{ fontSize: "16px", color: "#2F4D8B" }}
				/>
				<Typography
					sx={{
						fontSize: "13px",
						fontWeight: 600,
						color: "#2F4D8B",
					}}
				>
					{label}
				</Typography>
				<Box sx={{ flex: 1 }} />
				<IconButton
					size="small"
					aria-label={
						isExpanded
							? t("worker-requests_hide-call")
							: t("worker-requests_view-call")
					}
					sx={{ color: "#2F4D8B" }}
				>
					{isExpanded ? (
						<ExpandLess sx={{ fontSize: "18px" }} />
					) : (
						<ExpandMore sx={{ fontSize: "18px" }} />
					)}
				</IconButton>
			</Box>
			{/* unmountOnExit stops playback and drops audio memory when collapsed */}
			<Collapse in={isExpanded} unmountOnExit>
				<Box sx={{ padding: "4px 12px 12px 12px" }}>{children}</Box>
			</Collapse>
		</Box>
	);
};

interface CallDetailDrawerProps {
	open: boolean;
	requestId?: number | null;
	/** Call Logs keys playback off the call log entry id, not a request */
	callLogId?: number | null;
	conversationIds: string[];
	workerName: string;
	requestDate?: string;
	onClose: () => void;
}

const CallDetailDrawer: FC<CallDetailDrawerProps> = ({
	open,
	requestId,
	callLogId,
	conversationIds,
	workerName,
	requestDate,
	onClose,
}) => {
	const { t } = useTranslation();
	let calledOn = "";
	if (requestDate) {
		const calledDate = formatDate({
			dateStr: requestDate,
			formatType: "NUMERIC_DATE",
		});
		const calledTime = formatDate({
			dateStr: requestDate,
			formatType: "TIME",
		});
		calledOn = ` ${t("worker-requests_called-on", {
			date: calledDate,
			time: calledTime,
		})}`;
	}

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			// Rendered above dialogs so call details can be reviewed mid-approval
			sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
			PaperProps={{ sx: { width: "440px", maxWidth: "100%" } }}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: "16px",
					padding: "24px",
					fontFamily: "Roboto",
					height: "100%",
					boxSizing: "border-box",
					overflow: "hidden",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						gap: "12px",
					}}
				>
					<Box
						sx={{ display: "flex", flexDirection: "column", gap: "2px" }}
					>
						<Box
							sx={{ display: "flex", alignItems: "center", gap: "8px" }}
						>
							<PhoneCallbackOutlinedIcon
								sx={{ fontSize: "18px", color: "#2F4D8B" }}
							/>
							<Typography
								sx={{ fontSize: "16px", fontWeight: 600 }}
							>
								{t("worker-requests_call-request")}
							</Typography>
						</Box>
						<Typography sx={{ fontSize: "13px", color: "#5F6368" }}>
							{workerName}
							{calledOn}
						</Typography>
					</Box>
					<IconButton
						onClick={onClose}
						aria-label={t("worker-requests_close-call-details")}
						size="small"
					>
						<CloseOutlined
							sx={{ fontSize: "20px", color: "#5F6368" }}
						/>
					</IconButton>
				</Box>
				{(requestId != null || callLogId != null) &&
				conversationIds.length > 0 ? (
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							overflowY: "auto",
							display: "flex",
							flexDirection: "column",
							gap: "24px",
							paddingRight: "4px",
						}}
					>
						{conversationIds.map((conversationId, index) =>
							conversationIds.length > 1 ? (
								<CallGroup
									key={conversationId}
									label={t("worker-requests_call-index-of-total", {
										index: index + 1,
										total: conversationIds.length,
									})}
									defaultExpanded={
										index === conversationIds.length - 1
									}
								>
									<CallConversation
										requestId={requestId ?? undefined}
										callLogId={callLogId ?? undefined}
										conversationId={conversationId}
										workerName={workerName.split(" ")[0]}
										stickyPlayer
									/>
								</CallGroup>
							) : (
								<CallConversation
									key={conversationId}
									requestId={requestId ?? undefined}
									callLogId={callLogId ?? undefined}
									conversationId={conversationId}
									workerName={workerName.split(" ")[0]}
									stickyPlayer
								/>
							)
						)}
					</Box>
				) : (
					<Typography sx={{ fontSize: "13px", color: "#5F6368" }}>
						{t("worker-requests_no-call-recording")}
					</Typography>
				)}
			</Box>
		</Drawer>
	);
};

export default CallDetailDrawer;
